/**
 * IconPicker — chọn icon (lucide) + màu nền cho approval template.
 * Dùng trong Tab 1 "Thông tin".
 */
import { useState, useRef, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { Pencil } from 'lucide-react';

import { Trans } from '@lingui/react/macro';

// Curated icon list — các icon hợp với approval template
const ICONS = [
  'FileText', 'FileCheck', 'FilePlus', 'FileSignature', 'ClipboardCheck', 'ClipboardList',
  'ShoppingCart', 'Package', 'Truck', 'Receipt', 'CreditCard', 'Wallet',
  'DollarSign', 'Banknote', 'Coins', 'PiggyBank', 'TrendingUp', 'BarChart3',
  'Briefcase', 'Building2', 'Users', 'UserPlus', 'UserCheck', 'UserCog',
  'Calendar', 'Clock', 'AlarmClock', 'CalendarCheck', 'CalendarPlus',
  'Plane', 'Car', 'MapPin', 'Globe', 'Map',
  'Megaphone', 'Bell', 'Mail', 'MessageSquare', 'Phone',
  'Settings', 'Wrench', 'Hammer', 'Cog', 'Key',
  'Shield', 'AlertCircle', 'CheckCircle', 'XCircle', 'Info',
  'Heart', 'Star', 'Award', 'Trophy', 'Gift',
  'Laptop', 'Monitor', 'Smartphone', 'HardDrive', 'Cpu', 'Server',
  'Rocket', 'Zap', 'Flame', 'Lightbulb', 'Target',
  'Book', 'BookOpen', 'GraduationCap', 'School',
  'Home', 'Hotel', 'Coffee', 'Utensils',
  'Stethoscope', 'Pill', 'Activity',
];

const COLORS = [
  { key: 'blue', cls: 'bg-blue-500' },
  { key: 'green', cls: 'bg-green-500' },
  { key: 'red', cls: 'bg-red-500' },
  { key: 'orange', cls: 'bg-orange-500' },
  { key: 'yellow', cls: 'bg-yellow-500' },
  { key: 'purple', cls: 'bg-purple-500' },
  { key: 'pink', cls: 'bg-pink-500' },
  { key: 'teal', cls: 'bg-teal-500' },
  { key: 'indigo', cls: 'bg-indigo-500' },
  { key: 'gray', cls: 'bg-gray-500' },
];

export const COLOR_CLASS_BY_KEY = Object.fromEntries(COLORS.map(c => [c.key, c.cls]));

export function getColorClass(key) {
  return COLOR_CLASS_BY_KEY[key] || COLOR_CLASS_BY_KEY.blue;
}

export function getIconComponent(iconName) {
  return LucideIcons[iconName] || LucideIcons.FileText;
}

export default function IconPicker({ icon, bgColor, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const Icon = getIconComponent(icon);
  const colorCls = getColorClass(bgColor);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="group relative w-16 h-16 rounded-2xl flex items-center justify-center hover:opacity-90 transition shadow-sm"
        style={{}}>
        <span className={`absolute inset-0 rounded-2xl ${colorCls}`}></span>
        <Icon size={28} className="relative text-white" strokeWidth={2.2} />
        <span className="absolute -bottom-1 -right-1 bg-white border border-gray-200 rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
          <Pencil size={11} className="text-gray-500" />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 left-0 top-20 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 w-[420px]">
          <div className="mb-3">
            <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5"><Trans>Màu nền</Trans></p>
            <div className="flex gap-1.5">
              {COLORS.map(c => (
                <button key={c.key} type="button" onClick={() => onChange({ icon, bgColor: c.key })}
                  className={`w-7 h-7 rounded-lg ${c.cls} transition ${bgColor === c.key ? 'ring-2 ring-offset-2 ring-gray-400' : 'hover:scale-110'}`} />
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5"><Trans>Biểu tượng</Trans></p>
            <div className="grid grid-cols-10 gap-1 max-h-72 overflow-y-auto">
              {ICONS.map(name => {
                const I = getIconComponent(name);
                const selected = name === icon;
                return (
                  <button key={name} type="button" onClick={() => onChange({ icon: name, bgColor })}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition ${selected ? 'bg-blue-50 ring-2 ring-blue-400' : 'hover:bg-gray-100'}`}
                    title={name}>
                    <I size={16} className={selected ? 'text-blue-600' : 'text-gray-600'} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
